import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { Button } from './ui/button';
import { BookOpen, User, FileText, Users, Tag, LogOut } from 'lucide-react';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { getPatientLabelPlural, getPatientSlug } = useSettings();
  
  const patientSlug = getPatientSlug();
  const patientPath = `/${patientSlug}`;
  
  // Redirect if on wrong patient slug - with guard to prevent loops
  useEffect(() => {
    const currentPath = location.pathname.split('/')[1];
    const validSlugs = ['eleves', 'clients', 'patients', 'beneficiaires', 'apprenants', 'employes'];
    
    // Only redirect if we're on a valid patient slug but it's not the current one
    if (validSlugs.includes(currentPath) && currentPath !== patientSlug) {
      navigate(`/${patientSlug}`, { replace: true });
    }
  }, [patientSlug, location.pathname, navigate]);
  
  const isActive = (path) => location.pathname === path;
  
  const handleLogout = () => {
    if (window.confirm('Etes-vous sur de vouloir vous deconnecter ?')) {
      logout();
    }
  };
  
  return (
    <nav className="relative overflow-hidden shadow-lg" style={{
      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
      backdropFilter: 'blur(20px)',
      borderBottom: '2px solid rgba(102, 126, 234, 0.1)',
    }}>
      <div className="absolute inset-0 opacity-5" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center space-x-8">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              }}>
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent" data-testid="app-title">
                  ConfiData Pro
                </h1>
                <p className="text-xs text-gray-600 font-medium">Gestion des {getPatientLabelPlural()}</p>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Link
                to={patientPath}
                className={`group px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive(patientPath) 
                    ? 'text-white shadow-lg' 
                    : 'text-gray-700 hover:text-white hover:shadow-lg'
                }`}
                style={isActive(patientPath) ? {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                } : {}}
                onMouseEnter={(e) => {
                  if (!isActive(patientPath)) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(patientPath)) {
                    e.currentTarget.style.background = '';
                  }
                }}
                data-testid="nav-eleves"
              >
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Mes {getPatientLabelPlural()}
                </div>
              </Link>
              
              <Link
                to="/notes"
                className={`group px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive('/notes') 
                    ? 'text-white shadow-lg' 
                    : 'text-gray-700 hover:text-white hover:shadow-lg'
                }`}
                style={isActive('/notes') ? {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                } : {}}
                onMouseEnter={(e) => {
                  if (!isActive('/notes')) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/notes')) {
                    e.currentTarget.style.background = '';
                  }
                }}
                data-testid="nav-notes"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Mes notes
                </div>
              </Link>
              
              <Link
                to="/groupes"
                className={`group px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive('/groupes') 
                    ? 'text-white shadow-lg' 
                    : 'text-gray-700 hover:text-white hover:shadow-lg'
                }`}
                style={isActive('/groupes') ? {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                } : {}}
                onMouseEnter={(e) => {
                  if (!isActive('/groupes')) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/groupes')) {
                    e.currentTarget.style.background = '';
                  }
                }}
                data-testid="nav-groupes"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Mes groupes
                </div>
              </Link>
              
              <Link
                to="/reglages"
                className={`group px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActive('/reglages') 
                    ? 'text-white shadow-lg' 
                    : 'text-gray-700 hover:text-white hover:shadow-lg'
                }`}
                style={isActive('/reglages') ? {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                } : {}}
                onMouseEnter={(e) => {
                  if (!isActive('/reglages')) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/reglages')) {
                    e.currentTarget.style.background = '';
                  }
                }}
                data-testid="nav-reglages"
              >
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Réglages
                </div>
              </Link>
            </div>
          </div>
          
          <div className="flex flex-col items-end space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-red-600 hover:text-white border-2 border-red-200 hover:border-red-500 transition-all"
              data-testid="logout-button"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '';
              }}
            >
              <LogOut className="h-4 w-4" />
              Déconnexion
            </Button>
            <div className="text-sm px-4 py-2 rounded-lg max-w-xs" style={{
              background: 'linear-gradient(135deg, #f0f4ff 0%, #f5f3ff 100%)',
              border: '2px solid rgba(102, 126, 234, 0.2)',
            }}>
              Connecté : <span className="font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent truncate inline-block max-w-[200px] align-bottom" title={user?.username}>{user?.username}</span>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
